import { Pedido } from '../../Entity/Pedido';
import { formatarDinheiro } from '../../helper';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Divider,
  Stack,
  CircularProgress,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ModalGeneric from '../../Components/generic/ModalGeneric';
import { useEffect, useState } from 'react';
import pedidoApi from '../../api/apiPedido';
import { ItemPedidoCompletoType, PedidoCompletoEDClienteType } from '../../types/PedidoTypes';
import { toast } from 'react-toastify';
import LinearProgress from '@mui/joy/LinearProgress';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ResumoPagamento from './ResumoPagamentoPedidoCliente';
import EdButton from '../../Generic/EdButton';
import { useSessao } from '../../redux/sessao/sessaoHooks';
import EdTabela from '../../Generic/EdTabela/EdTabela';
import Coluna from '../../Generic/EdTabela/Coluna';
import EdModal from '../../Components/generic/EdModal';

type Props = {
  pedidoSelecionado: Pedido;
  onClose: () => void;
  open: boolean;
};

const ModalDetalhesPedido: React.FC<Props> = ({ pedidoSelecionado, onClose, open }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [items, setItems] = useState<ItemPedidoCompletoType[]>([]);
  const [pedidoCompleto, setPedidoCompleto] = useState<PedidoCompletoEDClienteType | null>(null);
  const [loading, setLoading] = useState(true);
  const [imprimindo, setImprimindo] = useState(false);
  const [sessao] = useSessao();
  const corPrincipal = sessao.empresa?.corPrincipal ?? '#1976d2';

  const cabecalhoTabela = [
    new Coluna<ItemPedidoCompletoType>({
      label: 'Descrição',
      render: (pedido) => {
        if (!pedido.descricao) return '';
        return <p>{pedido.descricao}</p>;
      },
      align: 'left'
    }),
    new Coluna<ItemPedidoCompletoType>({
      label: 'Quantidade',
      render: (pedido) => pedido.quantidade,
      align: 'center'
    }),
    new Coluna<ItemPedidoCompletoType>({
      label: '(R$) Total',
      render: (pedido) => {
        return (
          <div>
            <div>{formatarDinheiro(pedido.preco * pedido.quantidade)}</div>
          </div>
        );
      },
      align: 'right'
    })
  ];

  const getPedido = async () => {
    setLoading(true);
    try {
      const res = await pedidoApi.getOneCompleto(pedidoSelecionado.numeroPedido, true, '001');
      setPedidoCompleto(res);
      setItems(res.produtos);
    } catch (err) {
      toast.error('Erro ao buscar pedido.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) getPedido();
  }, [open, pedidoSelecionado.numeroPedido]);

  const baixarDav = async () => {
    if (!pedidoCompleto?.numeroPedido) return;
    setImprimindo(true);
    try {
      await pedidoApi.downloadDav(pedidoCompleto.numeroPedido);
      toast.success('PDF gerado!');
    } catch (erro) {
      toast.error('Erro ao imprimir');
    } finally {
      setImprimindo(false);
    }
  };

  return (
    <EdModal open={open} onClose={onClose}>
      <Box sx={{ p: isMobile ? 0 : 1, position: 'relative', minHeight: '200px' }}>
        {imprimindo && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CircularProgress size={30} />
            <Typography variant="caption" sx={{ mt: 1, fontWeight: 'bold' }}>
              GERANDO DOCUMENTO...
            </Typography>
          </Box>
        )}

        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="caption" color="textSecondary">
              PEDIDO
            </Typography>
            <Typography variant="h6" fontWeight="bold" lineHeight={1.2}>
              #{pedidoSelecionado.numeroPedido}
            </Typography>
          </Box>
          <EdButton
            size="small"
            variant="contained"
            color={corPrincipal}
            startIcon={<PictureAsPdfIcon />}
            onClick={baixarDav}
            disabled={loading || imprimindo}
            sx={{ borderRadius: 2 }}
          >
            {imprimindo ? '...' : 'PDF'}
          </EdButton>
        </Box>

        {loading ? (
          <Box py={4} textAlign="center">
            <LinearProgress color="primary" sx={{ height: 2, borderRadius: 1 }} />
            <Typography variant="caption" color="textSecondary" mt={1} display="block">
              Carregando detalhes...
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Stack spacing={1.5}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fcfcfc' }}>
                  <Typography variant="caption" color="textSecondary" display="block">
                    VENDEDOR
                  </Typography>
                  <Typography variant="body2" fontWeight="600" mb={1}>
                    {pedidoCompleto?.nomeVendedor || 'N/A'}
                  </Typography>
                  <Divider />
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    OPERAÇÃO
                  </Typography>
                  <Typography variant="body2" noWrap>
                    {pedidoCompleto?.nomeCfgPedido}
                  </Typography>
                </Paper>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    borderLeft: '4px solid',
                    borderLeftColor: 'primary.main'
                  }}
                >
                  <Stack spacing={0.5}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption">Subtotal</Typography>
                      <Typography variant="caption" fontWeight="500">
                        {formatarDinheiro(pedidoCompleto?.totalBruto ?? 0)}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption" color="error">
                        Descontos
                      </Typography>
                      <Typography variant="caption" color="error">
                        -{formatarDinheiro(pedidoCompleto?.descontoGeral ?? 0)}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" pt={0.5}>
                      <Typography variant="body1" fontWeight="bold">
                        Total Líquido
                      </Typography>
                      <Typography variant="body1" fontWeight="bold" color="primary">
                        {formatarDinheiro(
                          (pedidoCompleto?.totalBruto ?? 0) - (pedidoCompleto?.descontoGeral ?? 0)
                        )}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>

                <ResumoPagamento pedido={pedidoCompleto} />
              </Stack>
            </Grid>

            <Grid item xs={12} md={8}>
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                mb={1}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                ITENS DO PEDIDO{' '}
                <Box sx={{ fontSize: '0.7rem', bgcolor: '#eee', px: 1, borderRadius: 1 }}>
                  {items.length}
                </Box>
              </Typography>

              {!isMobile ? (
                <Box sx={{ height: 350, width: '100%' }}>
                  <EdTabela
                    chave={'TabelaEdClienteDetalhesPedido'}
                    cabecalhoTabela={cabecalhoTabela}
                    linhas={items}
                    loading={loading}
                    compactar
                    esconderToolbar
                    elevation={0}
                    maxHeight={350}
                  />
                </Box>
              ) : (
                <Stack spacing={1}>
                  {items.map((item) => (
                    <Paper key={item.codigo} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: 'text.primary' }}>
                        {item.descricao}
                      </Typography>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                        <Typography variant="caption" color="textSecondary">
                          {item.quantidade} un x {formatarDinheiro(item.preco)}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          {formatarDinheiro(item.preco * item.quantidade)}
                        </Typography>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Grid>
          </Grid>
        )}
      </Box>
    </EdModal>
  );
};

export default ModalDetalhesPedido;
